import {MemberAsSupplierView} from "./view/MemberAsSupplierView";
import {requestService} from "../../services/RequestService";
import {ApiRoutes} from "../../routes/ApiRoutes";
import {MemberContactUpdateDTO} from "./dto/MemberContactUpdateDTO";
import {MemberContactView} from "./view/MemberContactView";
import {BbxRequest} from "../../data/Request/BbxRequest";
import {MemberSearchRequestDTO} from "./dto/MemberSearchRequestDTO";
import {Page} from "../../data/Page";
import {MemberSearchResultView} from "./view/MemberSearchResultView";
import {MemberAutocompleteView} from "./view/MemberAutocompleteView";
import {GraydonUpdateDTO} from "./dto/GraydonUpdateDTO";
import {FileRequestDTO} from "../../data/Request/FileRequestDTO";
import {MemberRegistrationUpdateDTO} from "./dto/MemberRegistrationUpdateDTO";
import {SupplierSearchRequestDTO} from "./dto/SupplierSearchRequestDTO";
import {SearchSupplierResultView} from "./view/SearchSupplierResultView";
import {BlockUserUpdateDTO} from "./dto/BlockUserUpdateDTO";
import {MemberForSalesSuppliersListDTO} from "./dto/MemberForSalesSuppliersListDTO";
import {MemberForSalesSuppliersListView} from "./view/MemberForSalesSuppliersListView";
import {Modulo97CheckListDTO} from "./dto/Modulo97CheckListDTO";
import {Modulo97CheckView} from "./view/Modulo97CheckView";
import {UserSimilaritiesRequestDTO} from "./dto/UserSimilaritiesRequestDTO";
import {UserSimilaritiesListView} from "./view/UserSimilaritiesListView";
import {MemberForPartnerEditView} from "./view/MemberForPartnerEditView";
import {SupplierRelaunchRequestDTO} from "./dto/SupplierRelaunchRequestDTO";
import {SupplierRelaunchListView} from "./view/SupplierRelaunchListView";

export const MemberModelAPI = {
    /**
     * Fetches a member (as supplier) by their ID.
     * @param {number} id - The ID of the member.
     * @returns {Promise<MemberAsSupplierView>} Promise resolving to a MemberAsSupplierView.
     */
    findById: async (id: number): Promise<MemberAsSupplierView> => {
        const response = await requestService.get(ApiRoutes.REACT_APP_URL_MEMBER + "/" + id, {});
        return new MemberAsSupplierView(response.data);

    },

    /**
     * Fetches a member for editing by their ID, specifically for the lead edit variant.
     * This is used when a partner wants to edit a member's information.
     * @param id
     * @return Promise<MemberForPartnerEditView>
     */
    getMemberForEditById: async (id: number): Promise<MemberForPartnerEditView> => {

        if (!ApiRoutes.REACT_APP_URL_MEMBER) {
            throw new Error("REACT_APP_URL_MEMBER is not defined");
        }

        return requestService.get(ApiRoutes.REACT_APP_URL_MEMBER + "/" + id + "/lead-edit-variant").then((response) => {
            return new MemberForPartnerEditView(response.data);
        });
    },

    /**
     * Updates a member's contact information.
     * @param {MemberContactUpdateDTO} request - The request DTO containing the new contact information.
     * @returns {Promise<boolean>} Promise resolving to true if the update was successful.
     */
    updateMemberContact: async (request: MemberContactUpdateDTO): Promise<boolean> => {
        const response = await requestService.post(ApiRoutes.REACT_APP_URL_MEMBER_UPDATE, request);

        return response.data;
    },

    /**
     * Fetches a member's contact information by their ID.
     * @param {number} id - The ID of the member.
     * @returns {Promise<MemberContactView>} Promise resolving to a MemberContactView.
     */
    getMemberContactById: async (id: number): Promise<MemberContactView> => {
        const response = await requestService.get(ApiRoutes.REACT_APP_URL_MEMBER_READ + id, {});

        return new MemberContactView(response.data);
    },

    /**
     * Searches for members based on the provided request criteria.
     * @param {BbxRequest<MemberSearchRequestDTO>} request - The request object containing filter and pagination info.
     * @returns {Promise<Page<MemberSearchResultView>>} Promise resolving to a Page of MemberSearchResultView.
     */
    searchMembers: async (request: BbxRequest<MemberSearchRequestDTO>): Promise<Page<MemberSearchResultView>> => {
        const response = await requestService.post(ApiRoutes.REACT_APP_URL_MEMBER_SEARCH, request);

        return new Page<MemberSearchResultView>(MemberSearchResultView, response.data);
    },

    /**
     * Searches for members by name (autocomplete).
     * @param {string} name - The name to search for.
     * @param userGroupIds
     * @returns {Promise<Page<MemberAutocompleteView>>} Promise resolving to a Page of MemberAutocompleteView.
     */
    memberAutocomplete: async (name: string, userGroupIds: number[] = []): Promise<Page<MemberAutocompleteView>> => {
        const params = userGroupIds && userGroupIds.length > 0 ? { userGroupIds } : {};
        const response = await requestService.get(ApiRoutes.REACT_APP_URL_MEMBER_AUTOCOMPLETE + name, params);

        return new Page<MemberAutocompleteView>(MemberAutocompleteView, response.data);
    },

    /**
     * Updates a member's Graydon score.
     * @param {GraydonUpdateDTO} graydonUpdateDTO - The DTO containing Graydon score data.
     * @returns {Promise<boolean>} Promise resolving to true if the update was successful.
     */
    updateGraydonScore: async (graydonUpdateDTO: GraydonUpdateDTO): Promise<boolean> => {
        const response = await requestService.post(ApiRoutes.REACT_APP_URL_MEMBER_GRAYDON_SCORE, graydonUpdateDTO);

        return response.data;
    },

    /**
     * Updates a member's company logo.
     * @param {FileRequestDTO} fileRequestDTO - The DTO containing file data.
     * @returns {Promise<string>} Promise resolving to the URL of the updated logo.
     */
    updateCompanyLogo: async (fileRequestDTO: FileRequestDTO): Promise<string> => {
        const response = await requestService.post(ApiRoutes.REACT_APP_URL_MEMBER_UPDATE_LOGO, fileRequestDTO);

        return response.data;
    },

    /**
     * Updates a member's user group (prospect to teasing and so on...)
     * @param {MemberContactUpdateDTO} request - The request DTO containing the user group information.
     * @returns {Promise<boolean>} Promise resolving to true if the update was successful.
     */
    switchUserGroup: async (request: MemberContactUpdateDTO): Promise<boolean> => {
        const response = await requestService.post(ApiRoutes.REACT_APP_URL_MEMBER_SWITCH_USER_GROUP, request);

        return response.data;
    },

    /**
     * Creates a new member.
     * @param request
     * @return Promise<number>
     */
    createMember: async (request: MemberRegistrationUpdateDTO): Promise<number> => {
        const response = await requestService.post(ApiRoutes.REACT_APP_URL_MEMBER_CREATE, request);

        return response.data;
    },

    /**
     * Checks whether a user already exists with the given email.
     * @param email
     * @return Promise<boolean>
     */
    emailExists: async (email: string): Promise<boolean> => {
        const response = await requestService.get(ApiRoutes.REACT_APP_URL_MEMBER_EMAIL_EXISTS, {email});

        return response.data;
    },

    /**
     * Checks whether a login already exists with the given login id.
     * @param loginId
     * @return Promise<boolean>
     */
    loginIdExists: async (loginId: string): Promise<boolean> => {
        const response = await requestService.get(ApiRoutes.REACT_APP_URL_MEMBER_LOGIN_EXISTS, {loginId});

        return response.data;
    },

    /**
     * Changes the account manager for a supplier.
     * @param {number} supplierId - The ID of the supplier.
     * @param {number} accountManagerId - The ID of the new account manager.
     * @returns {Promise<boolean>} Promise resolving to true if the change was successful.
     */
    changeAccountManager: async (supplierId: number, accountManagerId: number): Promise<boolean> => {
        const response = await requestService.put(ApiRoutes.REACT_APP_URL_MEMBER_UPDATE_ACCOUNT_MANAGER + "/" + supplierId + "/" + accountManagerId, {});

        return response.data;
    },

    /**
     * Searches for suppliers based on the provided request criteria.
     * @param request
     * @return Promise<Page<SearchSupplierResultView>>
     */
    searchSuppliers: async (request: BbxRequest<SupplierSearchRequestDTO>): Promise<Page<SearchSupplierResultView>> => {
        const response = await requestService.post(ApiRoutes.REACT_APP_URL_MEMBER_SEARCH_SUPPLIER, request);

        return response.data;
    },

    /**
     * Blocks or unblocks a member.
     * @param request
     * @param memberId
     * @return Promise<boolean>
     */
    blockOrUnblock: async (request: BlockUserUpdateDTO, memberId: number): Promise<boolean> => {
        const response = await requestService.put(ApiRoutes.REACT_APP_URL_MEMBER + `/${memberId}/block-unblock`, request);

        return response.data;
    },

    /**
     * Updates whether protests are allowed for a member.
     * @param isProtestAllowed
     * @param memberId
     * @return Promise<boolean>
     */
    updateProtestAllowed: async (isProtestAllowed: boolean, memberId: number): Promise<boolean> => {
        const response = await requestService.put(ApiRoutes.REACT_APP_URL_MEMBER + `/${memberId}/protest-allowed`, {isProtestAllowed});

        return response.data;
    },

    /**
     * Changes the block reason for a member.
     * @param request
     * @param memberId
     * @return Promise<boolean>
     */
    changeBlockReason: async (request: BlockUserUpdateDTO, memberId: number): Promise<boolean> => {
        const response = await requestService.put(ApiRoutes.REACT_APP_URL_MEMBER + `/${memberId}/change-block-reason`, request);

        return response.data;
    },

    /**
     * Changes the user status for a member.
     * @param memberId - The ID of the member.
     * @param newStatus - The new status value.
     * @returns {Promise<boolean>} Promise resolving to true if the change was successful.
     */
    changeUserStatus: async (memberId: number, newStatus: number): Promise<boolean> => {
        const response = await requestService.put(ApiRoutes.REACT_APP_URL_MEMBER + `/${memberId}/change-user-status/${newStatus}`, {});

        return response.data;
    },

    salesSuppliersList: async (request: BbxRequest<MemberForSalesSuppliersListDTO>): Promise<Page<MemberForSalesSuppliersListView>> => {
        const response = await requestService.post(ApiRoutes.REACT_APP_URL_MEMBER_SALES_SUPPLIERS_LIST, request);

        return new Page<MemberForSalesSuppliersListView>(MemberForSalesSuppliersListView, response.data);
    },

    /**
     * Fetches the paginated supplier/prospect relaunch list (dormant members to re-engage).
     * @param {BbxRequest<SupplierRelaunchRequestDTO>} request - filters (salesId, categoryId, languageId, date) + pagination.
     * @returns {Promise<Page<SupplierRelaunchListView>>} Promise resolving to a Page of SupplierRelaunchListView.
     */
    suppliersRelaunchList: async (request: BbxRequest<SupplierRelaunchRequestDTO>): Promise<Page<SupplierRelaunchListView>> => {
        const response = await requestService.post(ApiRoutes.REACT_APP_URL_MEMBER + "/suppliers-relaunch", request);

        return new Page<SupplierRelaunchListView>(SupplierRelaunchListView, response.data);
    },

    /**
     * Downloads the supplier/prospect relaunch list (current filters) as a CSV file.
     * @returns the raw axios response (response.data is a Blob).
     */
    suppliersRelaunchCsv: async (request: BbxRequest<SupplierRelaunchRequestDTO>): Promise<any> => {
        return await requestService.post(ApiRoutes.REACT_APP_URL_MEMBER + "/suppliers-relaunch/export-csv", request, { responseType: 'blob' });
    },

    /**
     * Checks invalid numbers via modulo 97.
     * @param {BbxRequest<Modulo97CheckListDTO>} request - The request object.
     * @returns {Promise<Page<Modulo97CheckView>>} Promise resolving to a page of results.
     */
    moduloCheck: async (request: BbxRequest<Modulo97CheckListDTO>): Promise<Page<Modulo97CheckView>> => {
        const response = await requestService.post(ApiRoutes.REACT_APP_URL_MEMBER + "/modulo-check", request);
        return response.data;
    },

    userSimilarities: async (request: BbxRequest<UserSimilaritiesRequestDTO>): Promise<Page<UserSimilaritiesListView>> => {
        const response = await requestService.post(ApiRoutes.REACT_APP_URL_MEMBER_USER_SIMILARITIES, request);

        return new Page<UserSimilaritiesListView>(UserSimilaritiesListView, response.data);
    },

};